import { isGuideActiveForSrc } from '@/utils/guides';
import { MediaGuideMetadata } from '@/utils/media/types';
import { useAudio } from '@rhg/hooks/use-audio';

interface SessionAudioProps {
    inProgressClinical?: boolean;
    inProgressDTC?: boolean;
    currentSession?: MediaGuideMetadata;
}

interface SessionAudioState {
    isActiveAudio: boolean;
    isPlaying: boolean;
}

/**
 * Now-playing state for a program/session card or guide row.
 *
 * Reads ONLY the low-frequency `activeSrc` / `isPaused` from the main audio
 * context — not `timingState` — so cards and rows re-render only on
 * play/pause/track-change, never at the ~4Hz `timeupdate` cadence. EVE-12668.
 */
export function useSessionAudio({
    inProgressClinical,
    inProgressDTC,
    currentSession,
}: SessionAudioProps): SessionAudioState {
    const audioContext = useAudio();

    if (!(inProgressClinical || inProgressDTC) || !audioContext) {
        return {
            isActiveAudio: false,
            isPlaying: false,
        };
    }

    const {
        controls: { activeSrc, isPaused },
    } = audioContext;

    const isActiveAudio = isGuideActiveForSrc(currentSession, activeSrc);
    const isPlaying = isActiveAudio && !isPaused;

    return {
        isActiveAudio,
        isPlaying,
    };
}
